// Client-side .ics generator for BBA bookings.
// Brunei has no DST (always UTC+8), so we convert booking local time
// to UTC by subtracting 8 hours and emit DTSTART/DTEND in UTC ("Z" suffix).

const BRUNEI_OFFSET_HOURS = 8;

function pad(n) {
  return String(n).padStart(2, '0');
}

function toUtcStamp(date) {
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

// Folds long ICS lines per RFC 5545 (75 octets, CRLF + space continuation).
function foldLine(line) {
  if (line.length <= 75) return line;
  const chunks = [];
  for (let i = 0; i < line.length; i += 73) {
    chunks.push((i === 0 ? '' : ' ') + line.slice(i, i + 73));
  }
  return chunks.join('\r\n');
}

function escapeIcsText(value) {
  return String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

/**
 * Build an .ics VCALENDAR string for a confirmed BBA booking.
 *
 * @param {object} args
 * @param {string} args.bookingDate "YYYY-MM-DD"
 * @param {string} args.timeSlot    "HH:MM-HH:MM" (start slot)
 * @param {number} args.duration    hours (>= 1)
 * @param {string} args.reference   human reference (e.g. "BBA-AB12CD34")
 * @param {string} [args.locationName]
 * @param {string} [args.locationAddress]
 * @param {number} [args.amount]    BND amount paid (for description)
 * @returns {string} ics text
 */
export function buildBookingIcs({
  bookingDate,
  timeSlot,
  duration = 1,
  reference,
  locationName,
  locationAddress,
  amount,
}) {
  const [hStr, mStr] = String(timeSlot).split('-')[0].split(':');
  const startLocal = new Date(`${bookingDate}T${pad(hStr)}:${pad(mStr)}:00Z`);
  // Shift back by Brunei offset so the absolute instant is correct in UTC.
  const startUtc = new Date(startLocal.getTime() - BRUNEI_OFFSET_HOURS * 3600 * 1000);
  const endUtc = new Date(startUtc.getTime() + Number(duration || 1) * 3600 * 1000);
  const stamp = toUtcStamp(new Date());

  const summary = 'Basketball Court Booking — BBA';
  const descriptionParts = [
    `Booking reference: ${reference}`,
    amount != null ? `Amount paid: BND ${Number(amount).toFixed(2)}` : null,
    'Please arrive 10 minutes before your booking time.',
  ].filter(Boolean);
  const location = [locationName, locationAddress].filter(Boolean).join(', ');

  const uid = `${reference || stamp}@bba.sportsbn`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Brunei Basketball Association//Court Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${toUtcStamp(startUtc)}`,
    `DTEND:${toUtcStamp(endUtc)}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    `DESCRIPTION:${escapeIcsText(descriptionParts.join('\n'))}`,
    location ? `LOCATION:${escapeIcsText(location)}` : null,
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeIcsText('BBA booking starts in 30 minutes')}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return lines.map(foldLine).join('\r\n');
}

/** Trigger a browser download of the given .ics content. */
export function downloadIcs(filename, icsText) {
  const blob = new Blob([icsText], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on next tick so Safari has time to consume the URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
